import { FC, useEffect, useMemo } from 'react';
import { connect, useForm } from 'react-bootstrap-formutil';
import { FormCheckType } from 'types/common';
import CHN, { getFieldsOfCHNDeliveryAddress } from './CHN';
import Common, { getFieldsOfCommonDeliveryAddress } from './Common';
import Common2, { getFieldsOfCommon2DeliveryAddress } from './Common2';
import { DeliveryAddressFormutil, DeliveryAddressProps, DeliveryAddressTypeEnum } from './type';
import USA, { getFieldsOfUSADeliveryAddress } from './USA';

/**
 *  @description 获取邮寄地址字段
 *  @param sameDeliveryAndResidence 一般认为邮寄地址与居住地址不一致时才需要展示邮寄地址相关字段，所以单独修改美国邮寄地址时也需要获取到邮寄地址字段，这时需要传0
 */
export function getFieldsOfDeliveryAddress(
    deliveryAddressType: DeliveryAddressTypeEnum,
    sameDeliveryAndResidence: FormCheckType | undefined,
    needDeliveryAddressType?: boolean
) {
    switch (deliveryAddressType) {
        case DeliveryAddressTypeEnum.USA:
            return getFieldsOfUSADeliveryAddress(sameDeliveryAndResidence, needDeliveryAddressType);
        case DeliveryAddressTypeEnum.COMMON:
            return getFieldsOfCommonDeliveryAddress(sameDeliveryAndResidence, needDeliveryAddressType);
        case DeliveryAddressTypeEnum.CHN:
            return getFieldsOfCHNDeliveryAddress(sameDeliveryAndResidence, needDeliveryAddressType);
        case DeliveryAddressTypeEnum.COMMON2:
            return getFieldsOfCommon2DeliveryAddress(sameDeliveryAndResidence, needDeliveryAddressType);
        default:
            const _checkNever: never = deliveryAddressType;

            return [];
    }
}

/**
 * @description 根据不同邮寄国家获取对应的邮寄地址类型：https://tigertech.feishu.cn/wiki/UfEKwz1gviZMaqkkZG6cORyvn2e
 */
export const getDeliveryAddressTypeByDeliveryCountry = (params: {
    isSSN: boolean;
    isFourChn?: boolean;
    /** 邮寄国家，对应不同的邮寄地址类型。 */
    deliveryCountry: string;
}) => {
    const { isSSN, isFourChn, deliveryCountry } = params;

    if (isSSN || isFourChn) {
        // SSN和非SSN四个中国是美国邮寄地址
        return DeliveryAddressTypeEnum.USA;
    }

    if (deliveryCountry === 'CHN') {
        return DeliveryAddressTypeEnum.CHN;
    }

    if (deliveryCountry === 'CAN') {
        // 非SSN加拿大邮寄地址格式使用COMMON2
        return DeliveryAddressTypeEnum.COMMON2;
    }

    if (deliveryCountry === 'USA') {
        return DeliveryAddressTypeEnum.USA;
    }

    return DeliveryAddressTypeEnum.COMMON;
};

const DeliveryAddress: FC<DeliveryAddressProps> = ({ isSSN, isFourChn, legalResidenceCountry, ...restProps }) => {
    const { $params, $setValues } = useForm<DeliveryAddressFormutil>();
    const { delivery_country: formDeliveryCountry = '', delivery_address_type: formDeliveryAddressType } = $params;

    // 根据当前邮寄国家计算对应的邮寄地址类型
    const curDeliveryAddressType = useMemo(
        () =>
            getDeliveryAddressTypeByDeliveryCountry({
                isSSN,
                isFourChn,
                deliveryCountry: formDeliveryCountry
            }),
        [isFourChn, isSSN, formDeliveryCountry]
    );

    useEffect(() => {
        // 更新当前计算得到的邮寄地址类型
        if (formDeliveryAddressType !== curDeliveryAddressType) {
            $setValues({
                delivery_address_type: curDeliveryAddressType
            });
        }
    }, [curDeliveryAddressType, formDeliveryAddressType, $setValues]);

    switch (curDeliveryAddressType) {
        case DeliveryAddressTypeEnum.USA:
            return <USA isSSN={isSSN} isFourChn={isFourChn} deliveryCountry={formDeliveryCountry} {...restProps} />;
        case DeliveryAddressTypeEnum.COMMON:
            return <Common deliveryCountry={formDeliveryCountry} {...restProps} />;
        case DeliveryAddressTypeEnum.CHN:
            return (
                <CHN
                    legalResidenceCountry={legalResidenceCountry}
                    deliveryCountry={formDeliveryCountry}
                    {...restProps}
                />
            );
        case DeliveryAddressTypeEnum.COMMON2:
            return <Common2 deliveryCountry={formDeliveryCountry} {...restProps} />;
        default:
            const _checkNever: never = curDeliveryAddressType;

            return null;
    }
};

export default connect(DeliveryAddress);
export * from './type';
export { default as SameDeliveryAndResidence } from './components/SameDeliveryAndResidence';
export { default as DeliveryCountry } from './components/DeliveryCountry';
